import React from 'react';
import { Box } from '@mui/material';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { asArray, getDataValue, getEntryTimestamp, limitRows, toFiniteNumber } from '../utils';

const Settings = {
  Name: 'Map',
  Display: true,
  options: {
    view: null,
    settings: [
      { id: 'number', type: 'number', name: 'Number of data points', min: 1, default: 300 },
      { id: 'zoom', type: 'number', name: 'Zoom', min: 1, max: 20, default: 13 },
      {
        id: 'datasets',
        type: 'array',
        settings: [
          { id: 'lat', type: 'text', name: 'Latitude path', placeholder: 'location.lat' },
          { id: 'long', type: 'text', name: 'Longitude path', placeholder: 'location.long' },
        ],
      },
    ],
    grid: false,
  },
};

const LAT_PATHS = ['lat', 'latitude', 'location.lat', 'location.latitude', 'gps.lat', 'gps.latitude'];
const LNG_PATHS = ['lng', 'long', 'longitude', 'location.lng', 'location.long', 'location.longitude', 'gps.lng', 'gps.long', 'gps.longitude'];

function firstNumber(entry, paths) {
  for (const path of paths) {
    const value = toFiniteNumber(getDataValue(entry, path));
    if (value !== null) return value;
  }
  return null;
}

const View = ({ DeviceData, Settings: userSettings = {} }) => {
  if (!asArray(DeviceData).length) return <Box>No data.</Box>;

  const dataset = asArray(userSettings.datasets)[0] || {};
  const latPaths = dataset.lat ? [dataset.lat, ...LAT_PATHS] : LAT_PATHS;
  const lngPaths = dataset.long ? [dataset.long, ...LNG_PATHS] : LNG_PATHS;

  const markers = limitRows(DeviceData, userSettings.number || 300)
    .map((entry) => {
      const lat = firstNumber(entry, latPaths);
      const lng = firstNumber(entry, lngPaths);
      const timestamp = getEntryTimestamp(entry);

      if (lat === null || lng === null) return null;
      return { lat, lng, timestamp };
    })
    .filter(Boolean)
    .reverse();

  if (!markers.length) return <Box>No location data available.</Box>;

  const latest = markers[markers.length - 1];
  const center = [latest.lat, latest.lng];
  const zoom = Number(userSettings.zoom) > 0 ? Number(userSettings.zoom) : 13;

  return (
    <Box sx={{ height: 400, width: '100%', mt: 2 }}>
      <MapContainer center={center} zoom={zoom} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {markers.map((marker, index) => (
          <Marker key={`${marker.lat}-${marker.lng}-${marker.timestamp || index}`} position={[marker.lat, marker.lng]}>
            <Popup>
              {marker.timestamp ? new Date(marker.timestamp).toLocaleString('en-AU') : 'Unknown time'}
              <br />
              Lat: {marker.lat}, Lng: {marker.lng}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </Box>
  );
};

export default {
  Settings,
  View,
};
